import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as tasksService from '@/services/tasksService'
import { useAuth } from '@/hooks/useAuth'
import { useLogAction } from '@/hooks/useLogAction'
import { TASKS_KEY, INFINITE_TASKS_KEY } from '@/constants/queryKeys'
import type { CareTask } from '@/types/models'

function invalidateTaskCaches(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [TASKS_KEY] })
  queryClient.invalidateQueries({ queryKey: [INFINITE_TASKS_KEY] })
}

export function useTasks() {
  const { churchId } = useAuth()

  return useQuery({
    queryKey: [TASKS_KEY, churchId],
    queryFn: () => tasksService.getTasks(churchId!),
    enabled: !!churchId,
  })
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: [TASKS_KEY, id],
    queryFn: () => tasksService.getTask(id!),
    enabled: !!id,
  })
}

export function useAddTask() {
  const queryClient = useQueryClient()
  const { churchId } = useAuth()
  const log = useLogAction()

  return useMutation({
    mutationFn: (
      data: Omit<
        CareTask,
        'id' | 'churchId' | 'createdAt' | 'updatedAt' | 'completedAt'
      >,
    ) => tasksService.addTask({ ...data, churchId: churchId! }),
    onSuccess: (taskId, variables) => {
      invalidateTaskCaches(queryClient)
      log({
        action: 'task.create',
        entityType: 'task',
        entityId: taskId,
        details: { title: variables.title },
      })
    },
  })
}

export function useUpdateTask() {
  const queryClient = useQueryClient()
  const log = useLogAction()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<Omit<CareTask, 'id'>>
    }) => tasksService.updateTask(id, data),
    onSuccess: (_, variables) => {
      invalidateTaskCaches(queryClient)
      log({ action: 'task.update', entityType: 'task', entityId: variables.id })
    },
  })
}

export function useDeleteTask() {
  const queryClient = useQueryClient()
  const log = useLogAction()

  return useMutation({
    mutationFn: (id: string) => tasksService.deleteTask(id),
    onSuccess: (_, id) => {
      invalidateTaskCaches(queryClient)
      log({ action: 'task.delete', entityType: 'task', entityId: id })
    },
  })
}
